import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeycloakEventType, KeycloakService } from '@vectronic/keycloak-angular';
import { filter, Subject, switchMap } from 'rxjs';

interface LoggedInUserState {
  authenticated: boolean;
  username: string;
  isAdmin: boolean;
  roles: string[];
}

@Injectable({ providedIn: 'root' })
export class LoggedInUserService {
  private readonly authService = inject(KeycloakService);

  // state
  private state = signal<LoggedInUserState>({
    authenticated: false,
    isAdmin: false,
    username: '',
    roles: [],
  });

  // selectors
  username = computed(() => this.state().username);
  isAdmin = computed(() => this.state().isAdmin);
  isAuthenticated = computed(() => this.state().authenticated);
  roles = computed(() => this.state().roles);

  // sources
  logout$ = new Subject<void>();

  private refreshToken$ = this.authService.keycloakEvents$.pipe(
    takeUntilDestroyed(),
    filter((event) => event.type === KeycloakEventType.OnTokenExpired),
    switchMap(() => this.authService.updateToken()),
  );

  constructor() {
    // reducers
    if (!this.authService.isLoggedIn()) {
      this.authService.login();
    }

    this.state.update((state) => ({
      ...state,
      authenticated: this.authService.isLoggedIn(),
      username: this.authService.getUsername(),
      roles: this.authService.getUserRoles(),
      isAdmin: this.authService.isUserInRole('ROLE_ADMIN'),
    }));

    this.refreshToken$.subscribe();

    this.logout$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.authService.logout(window.location.origin));
  }
}
